import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrgStore } from '../store/org';
import { vpApi, errorMessage } from '../api/velopulse';

const STATUS_PILL = { pending: '待確認', confirmed: '已寫入', discarded: '已捨棄' };

/**
 * 助理草稿卡：Chat 的寫入類操作一律先以草稿呈現，
 * 只有使用者按下「確認寫入」才會呼叫後端 confirm 並生效。
 * 必含：動作摘要、變更前後對照、影響組織名稱、確認／修改／捨棄。
 */
export default function DraftCard({ draft, onChange }) {
  const currentOrg = useOrgStore((s) => s.currentOrg);
  const [editing, setEditing] = useState(null); // 編輯中的 changes 副本
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const pending = draft.status === 'pending';
  // 防錯租戶：草稿所屬組織必須等於目前組織，否則不允許確認
  const orgMismatch = !!draft.org_id && draft.org_id !== currentOrg?.id;
  const target = draft.target || {};
  const link = target.id && (target.type === 'vehicle' ? `/records/${target.id}` : target.type === 'opportunity' ? `/opportunities/${target.id}` : null);

  const run = async (kind, fn) => {
    setBusy(kind); setError('');
    try { onChange(await fn()); setEditing(null); } catch (err) { setError(errorMessage(err, '操作失敗，草稿仍保留，請再試一次。')); } finally { setBusy(''); }
  };

  return (
    <div className={`vp-draft${draft.status === 'confirmed' ? ' done' : ''}${draft.status === 'discarded' ? ' discarded' : ''}`} role="group" aria-label={`${draft.title || '草稿'}（${STATUS_PILL[draft.status] || ''}）`}>
      <div className="hd"><span className="pill">{STATUS_PILL[draft.status] || '草稿'}</span> {draft.title || `草稿：${draft.summary || ''}`}</div>
      <div className="org">
        組織：<strong>{draft.org_name || currentOrg?.name}</strong>
        {target.type_label && ` · 類型：${target.type_label}`}
        {target.code && ` · 編號 ${target.code}`}
      </div>
      {draft.summary && <div style={{ marginBottom: 6 }}>動作：{draft.summary}</div>}
      {draft.sends_to_customer && pending && <div className="vp-send-warn">注意：確認後將傳送訊息給顧客</div>}

      <table>
        <tbody>
          <tr><td>欄位</td><td>變更前</td><td>變更後</td></tr>
          {(editing || draft.changes || []).map((c, i) => (
            <tr key={c.field || i}>
              <td>{c.label}</td>
              <td>{c.before}</td>
              <td>
                {editing
                  ? <input aria-label={`${c.label} 變更後`} value={c.value ?? ''} onChange={(e) => setEditing((arr) => arr.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
                  : <strong>{c.after}</strong>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {orgMismatch && pending && <div className="vp-error" style={{ margin: '0 0 10px' }}>這份草稿屬於另一個組織，無法在目前組織確認。請捨棄後重新提出。</div>}
      {error && <div className="vp-error" role="alert" style={{ margin: '0 0 10px' }}>{error}</div>}

      {pending && !editing && (
        <div className="btns">
          <button type="button" className="vp-btn ok" disabled={!!busy || orgMismatch} onClick={() => run('confirm', () => vpApi.confirmDraft(draft.id))}>{busy === 'confirm' ? '寫入中…' : '確認寫入'}</button>
          <button type="button" className="vp-btn" disabled={!!busy || orgMismatch} onClick={() => setEditing((draft.changes || []).map((c) => ({ ...c })))}>修改草稿</button>
          <button type="button" className="vp-btn danger" disabled={!!busy} onClick={() => run('discard', () => vpApi.discardDraft(draft.id))}>{busy === 'discard' ? '捨棄中…' : '捨棄'}</button>
        </div>
      )}
      {pending && editing && (
        <div className="btns">
          <button type="button" className="vp-btn primary" disabled={!!busy} onClick={() => run('save', () => vpApi.updateDraft(draft.id, editing))}>{busy === 'save' ? '更新中…' : '更新草稿'}</button>
          <button type="button" className="vp-btn" disabled={!!busy} onClick={() => setEditing(null)}>取消修改</button>
        </div>
      )}
      {draft.status === 'confirmed' && <div>✓ 已確認並寫入。{link && <> <Link to={link} style={{ color: 'var(--vp-brand)' }}>開啟紀錄</Link></>}</div>}
      {draft.status === 'discarded' && <div>這份草稿已捨棄，沒有任何資料被變更。</div>}
    </div>
  );
}
